import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import localUtil from '../../lib/util';
import { isInactive as isVisitorInactive, sortInactiveLast } from '../../lib/visitorInactive';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import { animalAge } from '../../lib/animalAge';

// Bottom-drawer picker for attaching a visitor (+ optional animal) to a calendar todo.
// Decoupled from newCalendarEvent/newSale: it captures the selection locally and hands it
// back through props.onSelect({ visitor, animal }), then closes itself. (Not `callback` —
// the Drawer wrapper reserves `callback` for its own no-arg on-close handler.)
@withText(props => ({
    visitorTitle: <Text id='dyrejournal.visitor.search-title'>Search for visitor</Text>,
}))
@observer
class CalendarTodoVisitorPicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            visitor: null,
            visitorAnimals: [],
        };
    }

    loadAnimals = async (visitorIds) => {
        const { animalStore } = this.props.stores;
        if (visitorIds.length > 0) {
            const response = await animalStore.load({
                query: { owner: visitorIds },
                skipUpdate: true,
            });
            this.setState({ visitorAnimals: response || [] });
        } else {
            this.setState({ visitorAnimals: [] });
        }
    };

    searchVisitorCallback = (visitor) => {
        if (visitor && visitor.id) {
            this.setState({ visitor });
            this.loadAnimals([visitor.id]);
        }
    };

    finish = (animal) => {
        const { onSelect, drawerLevel } = this.props;
        const { appState } = this.props.stores;
        const { visitor } = this.state;
        if (!visitor || !visitor.id) {
            return;
        }
        const visitorObject = {
            id: visitor.id,
            firstname: visitor.firstname,
            lastname: visitor.lastname,
            cellphone: visitor.cellphone,
            email: visitor.email,
        };
        const animalObject = animal ? {
            id: animal.id,
            name: animal.name,
            breed: animal.breed,
            species: animal.species,
            birth: animal.birth,
            chipId: animal.chipId,
        } : null;
        if (typeof onSelect === 'function') {
            onSelect({ visitor: visitorObject, animal: animalObject });
        }
        appState.toggleDrawer(false, drawerLevel);
    };

    onClickChooseAnimal = (e) => {
        const { id } = e.target.closest('.resultLine').dataset;
        const { visitorAnimals } = this.state;
        const idInt = parseInt(id, 10);
        const animal = visitorAnimals.find(a => a.id === idInt);
        this.finish(animal);
    };

    onClickUseOwnerOnly = () => {
        this.finish(null);
    };

    render() {
        const { visitorTitle } = this.props;
        const { visitorStore } = this.props.stores;
        const { visitor, visitorAnimals } = this.state;
        const animals = (visitorAnimals || []).filter(a => !a.isDececed && !a.isRelocated && !a.isInactive);

        return (
            <div class='px-3 py-2'>
                <DyrejournalSaleSearch
                    stores={this.props.stores}
                    key={'todo-visitor'}
                    title={visitorTitle}
                    holdingStore={visitorStore}
                    searchResultList={'visitorResults'}
                    filterResults={sortInactiveLast}
                    searchResultsLine={(e, idx, selectedIndex) => (
                        <div class={`d-flex flex-column px-3 py-1 ${idx === selectedIndex ? 'selected' : ''} ${isVisitorInactive(e) ? 'text-body-secondary' : ''}`}>
                            <div class='d-flex flex-row justify-content-between'>
                                <div>
                                    {localUtil.showName(e)}
                                    {isVisitorInactive(e) && <span class='badge bg-secondary ms-2'><Text id='input.visitor-isInactive'>Inactive</Text></span>}
                                </div>
                                <div class='ms-2'>{e?.cellphone}</div>
                            </div>
                            <div class='d-flex flex-row justify-content-between'>
                                <small class='font-weight-lighter'>{e?.address} {e?.postalcode} {e?.place}</small>
                            </div>
                        </div>
                    )}
                    callback={this.searchVisitorCallback}
                    skipSaleUpdate={true}
                />

                {visitor && visitor.id && (
                    <div class='mt-3'>
                        <div class='font-weight-lighter text-body-secondary'>
                            <small class='text-uppercase'>
                                <Text id='calendar.todoPicker.selected-visitor'>Selected customer</Text>
                            </small>
                        </div>
                        <div class='d-flex flex-column bg-body-secondary rounded-lg px-3 py-2 mt-1'>
                            <div class='d-flex flex-row justify-content-between'>
                                <div>
                                    <i class='fa-duotone fa-user me-2' />
                                    {localUtil.showName(visitor)}
                                </div>
                                <div class='ms-2'>{visitor.cellphone}</div>
                            </div>
                            <div class='d-flex flex-row justify-content-between'>
                                <small class='font-weight-lighter'>{visitor.address} {visitor.postalcode} {visitor.place}</small>
                            </div>
                        </div>
                        <div class='d-flex flex-row justify-content-between align-items-center mt-3'>
                            <div class='font-weight-lighter text-body-secondary'>
                                <small class='text-uppercase'>
                                    <Text id='dyrejournal.animal.owned-by-visitor'>Animals owned by visitor</Text>
                                </small>
                            </div>
                            <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.onClickUseOwnerOnly}>
                                <Text id='calendar.todoPicker.use-owner-only'>Use owner only</Text>
                            </button>
                        </div>
                        {animals.length > 0 ? (
                            <div class='d-flex flex-wrap overflow-auto mt-1' style='max-height: 200px;'>
                                {animals.map((e, idx) => (
                                    <small
                                        class={`${e.isLost ? 'bg-danger text-white' : 'bg-body-secondary'} resultLine d-flex justify-content-between me-2 mt-1 px-2 font-weight-normal rounded-lg`}
                                        style='cursor: pointer; width: 32%;'
                                        onClick={this.onClickChooseAnimal}
                                        data-idx={idx}
                                        data-id={e.id}
                                        data-name={e.name}
                                    >
                                        {e.isLost && <i class='fa-duotone fa-magnifying-glass-location me-1' />}
                                        <div class='flex-fill'>{e.name} ({e.species})</div>
                                        <div class='ms-2 text-end'><nobr>{e.chipId && <i class='fa-solid fa-microchip text-muted me-2' />} {e.birth && animalAge(e.birth)}</nobr></div>
                                    </small>
                                ))}
                            </div>
                        ) : (
                            <div class='text-body-secondary mt-1'>
                                <small><Text id='calendar.todoPicker.no-animals'>No animals registered for this customer</Text></small>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default CalendarTodoVisitorPicker;
